import React, { useState, useCallback, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Loading } from '@components/atoms/Loading';
import { Button } from '@components/atoms/Button';
import { EquipmentStatusBadge } from '@components/atoms/EquipmentStatusBadge';
import { WarehouseLayout } from '@components/layout/WarehouseLayout';
import { EquipmentAssetFormModal } from '@components/organisms/EquipmentAssetFormModal';
import { EquipmentCheckoutModal } from '@components/organisms/EquipmentCheckoutModal';
import { EquipmentReturnModal } from '@components/organisms/EquipmentReturnModal';
import { EquipmentReservationCalendar } from '@components/organisms/EquipmentReservationCalendar';
import { EquipmentMaintenanceModal } from '@components/organisms/EquipmentMaintenanceModal';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import {
  useEquipmentAsset,
  useEquipmentUsageHistory,
  useMaterialCategoriesByType,
  useWarehouses,
  useUpdateEquipmentAsset,
  useCheckoutEquipment,
  useReturnEquipment,
  useReserveEquipment,
  useCancelEquipmentReservation,
  useSetEquipmentMaintenance,
  useCompleteEquipmentMaintenance,
} from '@hooks/api/warehouse';
import {
  CategoryType,
  EquipmentAssetCreateInput,
  EquipmentAssetUpdateInput,
  EquipmentCheckoutInput,
  EquipmentReturnInput,
  EquipmentReserveInput,
  EquipmentMaintenanceInput,
  EquipmentStatus,
  EquipmentCondition,
} from '@models/warehouse.model';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import ConstructionIcon from '@mui/icons-material/Construction';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import CancelIcon from '@mui/icons-material/Cancel';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDateOnly, formatDateTime } from '@utils/formatDate';
import { toast } from 'react-toastify';
import { useTranslation } from '@hooks/useTranslation';

const ITEMS_PER_PAGE = 10;

const EquipmentAssetDetailPage: React.FC = () => {
  const tWarehouse = useTranslation('warehouse');
  const tCommon = useTranslation('common');
  const tEnumLabels = useTranslation('enumLabels');
  const { id: assetId } = useParams<{ id: string }>();
  const history = useHistory();

  // Modal states
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [isReserveModalOpen, setIsReserveModalOpen] = useState(false);
  const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState(false);
  const [reservationToCancel, setReservationToCancel] = useState<string | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // API Hooks
  const {
    data: asset,
    isLoading: assetLoading,
    isError: assetError,
  } = useEquipmentAsset(assetId);

  const {
    data: usageHistory = [],
    isLoading: usageLoading,
    isError: usageError,
  } = useEquipmentUsageHistory(assetId);

  const {
    data: categories = [],
  } = useMaterialCategoriesByType(CategoryType.EQUIPMENT);

  const {
    data: warehouses = [],
  } = useWarehouses();

  // Mutations
  const updateMutation = useUpdateEquipmentAsset();
  const checkoutMutation = useCheckoutEquipment();
  const returnMutation = useReturnEquipment();
  const reserveMutation = useReserveEquipment();
  const cancelReservationMutation = useCancelEquipmentReservation();
  const maintenanceMutation = useSetEquipmentMaintenance();
  const completeMaintenanceMutation = useCompleteEquipmentMaintenance();

  const isLoading = (assetLoading && !assetError);

  // Paginated usage history
  const paginatedUsage = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return usageHistory.slice(start, start + ITEMS_PER_PAGE);
  }, [usageHistory, currentPage]);

  const totalPages = Math.ceil(usageHistory.length / ITEMS_PER_PAGE);

  // Format helpers
  const formatCurrency = (value: number | undefined) => {
    if (value == null) return '--';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const formatCondition = (condition: EquipmentCondition | string | undefined) => {
    if (!condition) return '--';
    const map: Record<string, string> = {
      GOOD: tEnumLabels.equipmentConditionGood,
      FAIR: tEnumLabels.equipmentConditionFair,
      NEEDS_REPAIR: tEnumLabels.equipmentConditionNeedsRepair,
    };
    return map[condition] || condition;
  };

  // Handlers
  const handleBack = useCallback(() => {
    history.push('/warehouse/equipment');
  }, [history]);

  const handleEdit = useCallback(() => {
    setIsFormModalOpen(true);
  }, []);

  const handleCloseFormModal = useCallback(() => {
    setIsFormModalOpen(false);
  }, []);

  const handleSubmitForm = useCallback(
    async (data: EquipmentAssetCreateInput | EquipmentAssetUpdateInput) => {
      try {
        await updateMutation.mutateAsync(data as EquipmentAssetUpdateInput);
        toast.success(tWarehouse.equipmentDetail.updatedSuccess);
        handleCloseFormModal();
      } catch (error) {
        console.error('Error updating equipment:', error);
        toast.error(tWarehouse.equipmentDetail.updateFailed);
      }
    },
    [updateMutation, handleCloseFormModal, tWarehouse]
  );

  const handleCheckout = useCallback(() => {
    setIsCheckoutModalOpen(true);
  }, []);

  const handleCloseCheckoutModal = useCallback(() => {
    setIsCheckoutModalOpen(false);
  }, []);

  const handleSubmitCheckout = useCallback(
    async (input: EquipmentCheckoutInput) => {
      try {
        await checkoutMutation.mutateAsync(input);
        toast.success(tWarehouse.equipmentDetail.checkedOutSuccess);
        handleCloseCheckoutModal();
      } catch (error) {
        console.error('Error checking out equipment:', error);
        toast.error(tWarehouse.equipmentDetail.checkoutFailed);
      }
    },
    [checkoutMutation, handleCloseCheckoutModal, tWarehouse]
  );

  const handleReturn = useCallback(() => {
    setIsReturnModalOpen(true);
  }, []);

  const handleCloseReturnModal = useCallback(() => {
    setIsReturnModalOpen(false);
  }, []);

  const handleSubmitReturn = useCallback(
    async (input: EquipmentReturnInput) => {
      try {
        await returnMutation.mutateAsync(input);
        toast.success(tWarehouse.equipmentDetail.returnedSuccess);
        handleCloseReturnModal();
      } catch (error) {
        console.error('Error returning equipment:', error);
        toast.error(tWarehouse.equipmentDetail.returnFailed);
      }
    },
    [returnMutation, handleCloseReturnModal, tWarehouse]
  );

  const handleReserve = useCallback(() => {
    setIsReserveModalOpen(true);
  }, []);

  const handleCloseReserveModal = useCallback(() => {
    setIsReserveModalOpen(false);
  }, []);

  const handleSubmitReserve = useCallback(
    async (input: EquipmentReserveInput) => {
      try {
        await reserveMutation.mutateAsync(input);
        toast.success(tWarehouse.equipmentDetail.reservedSuccess);
        handleCloseReserveModal();
      } catch (error) {
        console.error('Error reserving equipment:', error);
        toast.error(tWarehouse.equipmentDetail.reserveFailed);
      }
    },
    [reserveMutation, handleCloseReserveModal, tWarehouse]
  );

  const handleCancelReservation = useCallback(async () => {
    if (!reservationToCancel) return;
    try {
      await cancelReservationMutation.mutateAsync(reservationToCancel);
      toast.success(tWarehouse.equipmentDetail.reservationCancelledSuccess);
      setReservationToCancel(null);
    } catch (error) {
      console.error('Error cancelling reservation:', error);
      toast.error(tWarehouse.equipmentDetail.cancelReservationFailed);
    }
  }, [reservationToCancel, cancelReservationMutation, tWarehouse]);

  const handleSetMaintenance = useCallback(() => {
    setIsMaintenanceModalOpen(true);
  }, []);

  const handleCloseMaintenanceModal = useCallback(() => {
    setIsMaintenanceModalOpen(false);
  }, []);

  const handleSubmitMaintenance = useCallback(
    async (input: EquipmentMaintenanceInput) => {
      try {
        await maintenanceMutation.mutateAsync(input);
        toast.success(tWarehouse.equipmentDetail.setMaintenanceSuccess);
        handleCloseMaintenanceModal();
      } catch (error) {
        console.error('Error setting maintenance:', error);
        toast.error(tWarehouse.equipmentDetail.setMaintenanceFailed);
      }
    },
    [maintenanceMutation, handleCloseMaintenanceModal, tWarehouse]
  );

  const handleCompleteMaintenance = useCallback(async () => {
    if (!asset) return;
    try {
      await completeMaintenanceMutation.mutateAsync(asset.assetId);
      toast.success(tWarehouse.equipmentDetail.completeMaintenanceSuccess);
    } catch (error) {
      console.error('Error completing maintenance:', error);
      toast.error(tWarehouse.equipmentDetail.completeMaintenanceFailed);
    }
  }, [asset, completeMaintenanceMutation, tWarehouse]);


  // Calculate total hours
  const totalHoursUsed = useMemo(() => {
    return usageHistory.reduce((sum, u) => sum + (u.hoursUsed || 0), 0);
  }, [usageHistory]);

  if (isLoading) {
    return (
      <WarehouseLayout>
        <div className="flex justify-center items-center h-full">
          <Loading size="xl" center />
        </div>
      </WarehouseLayout>
    );
  }

  if (!asset) {
    return (
      <WarehouseLayout>
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">{tWarehouse.equipmentDetail.notFound}</p>
          <Button variant="secondary" onClick={handleBack}>
            {tWarehouse.equipmentDetail.backToEquipment}
          </Button>
        </div>
      </WarehouseLayout>
    );
  }

  return (
    <WarehouseLayout>
      <div className="space-y-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <button type="button" onClick={handleBack} className="hover:text-gray-600">{tWarehouse.equipmentDetail.equipmentBreadcrumb}</button>
          <span>/</span>
          <span className="text-gray-500">{asset.name}</span>
          {asset.createdAt && (
            <>
              <span className="mx-1">·</span>
              <span>{tWarehouse.equipmentDetail.created} {formatDateOnly(asset.createdAt)}</span>
            </>
          )}
        </div>

        {/* Header */}
        <div className="flex items-center gap-4">
          <button type="button"
            onClick={handleBack}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </button>
          <div className="flex-1 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <ConstructionIcon className="text-indigo-600" sx={{ fontSize: 20 }} />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white leading-none">
                    {asset.name}
                  </h1>
                  <EquipmentStatusBadge status={asset.status} size="sm" />
                </div>
                <p className="text-sm text-gray-500 mt-0.5">
                  {asset.serialNumber || asset.assetCode || ''}
                  {asset.manufacturer ? ` · ${asset.manufacturer}` : ''}
                  {asset.model ? ` ${asset.model}` : ''}
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              {asset.status === EquipmentStatus.AVAILABLE && (
                <>
                  <Button
                    variant="primary"
                    leftIcon={<LogoutIcon sx={{ fontSize: 16 }} />}
                    onClick={handleCheckout}
                  >
                    {tWarehouse.equipmentDetail.checkout}
                  </Button>
                  <Button
                    variant="secondary"
                    leftIcon={<BookmarkIcon sx={{ fontSize: 16 }} />}
                    onClick={handleReserve}
                  >
                    {tWarehouse.equipmentDetail.reserve}
                  </Button>
                  <Button
                    variant="secondary"
                    leftIcon={<BuildIcon sx={{ fontSize: 16 }} />}
                    onClick={handleSetMaintenance}
                  >
                    {tWarehouse.equipmentDetail.maintenance}
                  </Button>
                </>
              )}
              {asset.status === EquipmentStatus.IN_USE && (
                <>
                  <Button
                    variant="primary"
                    leftIcon={<LoginIcon sx={{ fontSize: 16 }} />}
                    onClick={handleReturn}
                  >
                    {tWarehouse.equipmentDetail.return}
                  </Button>
                  <Button
                    variant="secondary"
                    leftIcon={<BuildIcon sx={{ fontSize: 16 }} />}
                    onClick={handleSetMaintenance}
                  >
                    {tWarehouse.equipmentDetail.maintenance}
                  </Button>
                </>
              )}
              {asset.status === EquipmentStatus.MAINTENANCE && (
                <Button
                  variant="primary"
                  leftIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                  onClick={handleCompleteMaintenance}
                >
                  {tWarehouse.equipmentDetail.completeMaintenance}
                </Button>
              )}
              {asset.status === EquipmentStatus.RESERVED && (
                <>
                  <Button
                    variant="primary"
                    leftIcon={<LogoutIcon sx={{ fontSize: 16 }} />}
                    onClick={handleCheckout}
                  >
                    {tWarehouse.equipmentDetail.checkout}
                  </Button>
                  <Button
                    variant="secondary"
                    leftIcon={<BookmarkIcon sx={{ fontSize: 16 }} />}
                    onClick={handleReserve}
                  >
                    {tWarehouse.equipmentDetail.reserve}
                  </Button>
                  <Button
                    variant="secondary"
                    leftIcon={<BuildIcon sx={{ fontSize: 16 }} />}
                    onClick={handleSetMaintenance}
                  >
                    {tWarehouse.equipmentDetail.maintenance}
                  </Button>
                </>
              )}
              <Button
                variant="secondary"
                leftIcon={<EditIcon sx={{ fontSize: 16 }} />}
                onClick={handleEdit}
              >
                {tCommon.edit}
              </Button>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
            <p className="text-xs text-gray-500 uppercase">{tWarehouse.equipmentDetail.serialNumber}</p>
            <p className="text-lg font-semibold text-gray-900 mt-1">{asset.serialNumber || '--'}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
            <p className="text-xs text-gray-500 uppercase">{tWarehouse.equipmentDetail.totalHoursUsed}</p>
            <p className="text-lg font-semibold text-gray-900 mt-1">{totalHoursUsed.toFixed(1)}h</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
            <p className="text-xs text-gray-500 uppercase">{tWarehouse.equipmentDetail.costPerHour}</p>
            <p className="text-lg font-semibold text-gray-900 mt-1">{formatCurrency(asset.hourlyRate)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
            <p className="text-xs text-gray-500 uppercase">{tWarehouse.equipmentDetail.pricePerHour}</p>
            <p className="text-lg font-semibold text-gray-900 mt-1">{formatCurrency(asset.clientHourlyRate)}</p>
          </div>
        </div>

        {/* Current Assignment Info - Only for IN_USE */}
        {asset.status === EquipmentStatus.IN_USE && (
          <div className="rounded-lg border p-4 flex items-start gap-3 bg-blue-50 border-blue-200">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-blue-100">
              <LogoutIcon className="text-blue-600" sx={{ fontSize: 16 }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-blue-900">{tWarehouse.equipmentDetail.currentlyInUse}</p>
              {asset.currentTask ? (
                <div className="mt-1 space-y-0.5">
                  <p className="text-sm text-gray-700">
                    <span className="text-gray-500">{tWarehouse.equipmentDetail.taskColon}</span>{' '}
                    <span className="font-medium">{asset.currentTask.name}</span>
                  </p>
                  {asset.currentTask.project && (
                    <p className="text-sm text-gray-700">
                      <span className="text-gray-500">{tWarehouse.equipmentDetail.projectColon}</span>{' '}
                      <span className="font-medium">{asset.currentTask.project.name}</span>
                    </p>
                  )}
                </div>
              ) : asset.currentTaskId ? (
                <p className="text-xs text-gray-500 mt-1">{tWarehouse.equipmentDetail.taskIdColon} {asset.currentTaskId}</p>
              ) : null}
            </div>
          </div>
        )}

        {/* Maintenance Info - Only for MAINTENANCE */}
        {asset.status === EquipmentStatus.MAINTENANCE && (
          <div className="rounded-lg border p-4 flex items-start gap-3 bg-orange-50 border-orange-200">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-orange-100">
              <BuildIcon className="text-orange-600" sx={{ fontSize: 16 }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-orange-900">{tWarehouse.equipmentDetail.currentlyInMaintenance}</p>
              <div className="mt-1 space-y-0.5">
                {asset.estimatedAvailableDate && (
                  <p className="text-sm text-gray-700">
                    <span className="text-gray-500">{tWarehouse.equipmentDetail.estimatedAvailableColon}</span>{' '}
                    <span className="font-medium">{formatDateOnly(asset.estimatedAvailableDate)}</span>
                  </p>
                )}
                {asset.maintenanceNotes && (
                  <p className="text-sm text-gray-700">
                    <span className="text-gray-500">{tWarehouse.equipmentDetail.notesColon}</span>{' '}
                    <span>{asset.maintenanceNotes}</span>
                  </p>
                )}
                {asset.lastMaintenanceDate && (
                  <p className="text-sm text-gray-700">
                    <span className="text-gray-500">{tWarehouse.equipmentDetail.sinceColon}</span>{' '}
                    <span className="font-medium">{formatDateOnly(asset.lastMaintenanceDate)}</span>
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Active Reservations */}
        {asset.reservations && asset.reservations.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-6">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
              {tWarehouse.equipmentDetail.activeReservations} ({asset.reservations.length})
            </h2>
            <div className="space-y-3">
              {asset.reservations.map((reservation) => {
                const isCheckout = reservation.isCheckoutReservation === true;
                return (
                  <div
                    key={reservation.reservationId}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      isCheckout
                        ? 'bg-blue-50 border-blue-100'
                        : 'bg-purple-50 border-purple-100'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isCheckout ? 'bg-blue-100' : 'bg-purple-100'
                      }`}>
                        {isCheckout ? (
                          <LogoutIcon className="text-blue-600" sx={{ fontSize: 16 }} />
                        ) : (
                          <BookmarkIcon className="text-purple-600" sx={{ fontSize: 16 }} />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {isCheckout && <span className="text-blue-600 mr-1">{tWarehouse.equipmentDetail.inUseTag}</span>}
                          {reservation.task?.name || 'Unknown task'}
                        </p>
                        {reservation.task?.project && (
                          <p className={`text-xs ${isCheckout ? 'text-blue-700' : 'text-purple-700'}`}>
                            <span className="text-gray-500">{tWarehouse.equipmentDetail.projectColon}</span>{' '}
                            {reservation.task.project.name}
                          </p>
                        )}
                        <p className="text-xs text-gray-500">
                          {formatDateTime(reservation.startDate)} — {formatDateTime(reservation.endDate)}
                        </p>
                      </div>
                    </div>
                    {!isCheckout && (
                      <Button
                        variant="secondary"
                        leftIcon={<CancelIcon sx={{ fontSize: 14 }} />}
                        onClick={() => setReservationToCancel(reservation.reservationId)}
                      >
                        {tCommon.cancel}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Details + Maintenance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Details */}
          <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-6">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">{tWarehouse.equipmentDetail.details}</h2>
            <dl className="space-y-3">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{tCommon.category}</dt>
                <dd className="font-medium text-gray-900">{asset.category?.name || '--'}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{tWarehouse.equipmentDetail.manufacturer}</dt>
                <dd className="font-medium text-gray-900">{asset.manufacturer || '--'}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{tWarehouse.equipmentDetail.model}</dt>
                <dd className="font-medium text-gray-900">{asset.model || '--'}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{tWarehouse.equipmentDetail.purchaseDate}</dt>
                <dd className="font-medium text-gray-900">
                  {asset.purchaseDate ? formatDateOnly(asset.purchaseDate) : '--'}
                </dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{tWarehouse.equipmentDetail.purchaseCost}</dt>
                <dd className="font-medium text-gray-900">{formatCurrency(asset.purchaseCost)}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{tWarehouse.equipmentDetail.storageLocation}</dt>
                <dd className="font-medium text-gray-900">{asset.warehouse?.name || '--'}</dd>
              </div>
            </dl>
          </div>

          {/* Maintenance */}
          <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-6">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">{tWarehouse.equipmentDetail.maintenance}</h2>
            <dl className="space-y-3">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{tWarehouse.equipmentDetail.lastMaintenance}</dt>
                <dd className="font-medium text-gray-900">
                  {asset.lastMaintenanceDate ? formatDateOnly(asset.lastMaintenanceDate) : '--'}
                </dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{tWarehouse.equipmentDetail.estimatedAvailable}</dt>
                <dd className="font-medium text-gray-900">
                  {asset.estimatedAvailableDate ? formatDateOnly(asset.estimatedAvailableDate) : '--'}
                </dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{tWarehouse.equipmentDetail.notes}</dt>
                <dd className="font-medium text-gray-900">{asset.maintenanceNotes || '--'}</dd>
              </div>
            </dl>
            {asset.description && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-1">{tCommon.description}</p>
                <p className="text-sm text-gray-700">{asset.description}</p>
              </div>
            )}
          </div>
        </div>

        {/* Reservation Calendar */}
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5">
          <button type="button"
            onClick={() => setIsCalendarOpen(!isCalendarOpen)}
            className="w-full flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors rounded-lg"
          >
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
              {tWarehouse.equipmentDetail.reservationCalendar}
            </h2>
            {isCalendarOpen ? (
              <ExpandLessOutlinedIcon className="text-gray-400" sx={{ fontSize: 20 }} />
            ) : (
              <ExpandMoreOutlinedIcon className="text-gray-400" sx={{ fontSize: 20 }} />
            )}
          </button>
          {isCalendarOpen && (
            <div className="px-6 pb-6">
              <EquipmentReservationCalendar assetId={assetId} height={500} />
            </div>
          )}
        </div>

        {/* Usage History */}
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
              {tWarehouse.equipmentDetail.usageHistory} ({usageHistory.length})
            </h2>
          </div>

          {(usageLoading && !usageError) ? (
            <div className="flex justify-center items-center p-8">
              <Loading size="lg" />
            </div>
          ) : usageHistory.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {tWarehouse.equipmentDetail.noUsageHistory}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">{tWarehouse.dashboard.date}</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">{tWarehouse.equipmentDetail.taskProject}</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">{tWarehouse.equipmentDetail.hours}</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">{tWarehouse.equipmentDetail.condition}</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">{tWarehouse.equipmentDetail.cost}</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">{tWarehouse.equipmentDetail.user}</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">{tWarehouse.equipmentDetail.notes}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedUsage.map((usage) => {
                      const hours = usage.hoursUsed || 0;
                      const cost = hours * (asset.hourlyRate || 0);

                      return (
                        <tr key={usage.usageId} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <div>{formatDateOnly(usage.checkoutDate)}</div>
                            {usage.returnDate ? (
                              <div className="text-xs text-gray-500">
                                to {formatDateOnly(usage.returnDate)}
                              </div>
                            ) : (
                              <div className="text-xs text-blue-600 font-medium">{tWarehouse.equipmentDetail.inUseLowercase}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <div className="font-medium">{usage.task?.name || '--'}</div>
                            {usage.task?.project && (
                              <div className="text-xs text-gray-500">{usage.task.project.name}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {hours > 0 ? `${hours}h` : '--'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {formatCondition(usage.condition)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {cost > 0 ? formatCurrency(cost) : '--'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {usage.checkoutUser
                              ? `${usage.checkoutUser.firstName} ${usage.checkoutUser.lastName}`
                              : '--'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                            {usage.notes || usage.checkoutNotes || '--'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200">
                  <p className="text-sm text-gray-500">
                    {tWarehouse.equipmentDetail.showing} {(currentPage - 1) * ITEMS_PER_PAGE + 1} {tWarehouse.equipmentDetail.to}{' '}
                    {Math.min(currentPage * ITEMS_PER_PAGE, usageHistory.length)} {tWarehouse.equipmentDetail.of} {usageHistory.length}
                  </p>
                  <div className="flex gap-1">
                    <button type="button"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button type="button"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Form Modal */}
        <EquipmentAssetFormModal
          isOpen={isFormModalOpen}
          onClose={handleCloseFormModal}
          onSubmit={handleSubmitForm}
          equipment={asset}
          categories={categories}
          warehouses={warehouses}
          isLoading={updateMutation.isPending}
        />

        {/* Checkout Modal */}
        <EquipmentCheckoutModal
          isOpen={isCheckoutModalOpen}
          onClose={handleCloseCheckoutModal}
          onCheckout={handleSubmitCheckout}
          equipment={asset}
          isLoading={checkoutMutation.isPending}
        />

        {/* Reserve Modal */}
        <EquipmentCheckoutModal
          isOpen={isReserveModalOpen}
          onClose={handleCloseReserveModal}
          onCheckout={handleSubmitCheckout}
          onReserve={handleSubmitReserve}
          equipment={asset}
          isLoading={reserveMutation.isPending}
          mode="reserve"
        />

        {/* Return Modal */}
        <EquipmentReturnModal
          isOpen={isReturnModalOpen}
          onClose={handleCloseReturnModal}
          onReturn={handleSubmitReturn}
          equipment={asset}
          isLoading={returnMutation.isPending}
        />

        {/* Maintenance Modal */}
        <EquipmentMaintenanceModal
          isOpen={isMaintenanceModalOpen}
          onClose={handleCloseMaintenanceModal}
          onConfirm={handleSubmitMaintenance}
          equipment={asset}
          isLoading={maintenanceMutation.isPending}
        />

        {/* Cancel Reservation Confirmation */}
        <ConfirmationModal
          isOpen={!!reservationToCancel}
          onClose={() => setReservationToCancel(null)}
          onConfirm={handleCancelReservation}
          title={tWarehouse.equipmentDetail.cancelReservationTitle}
          description={tWarehouse.equipmentDetail.cancelReservationDescription}
          confirmText={tWarehouse.equipmentDetail.cancelReservationTitle}
          variant="danger"
        />

      </div>
    </WarehouseLayout>
  );
};

export default EquipmentAssetDetailPage;
